// src/utils/GridColumnHelper.ts

import type {
    GridColDef,
    GridColType,
    GridRenderCellParams,
    GridTreeNodeWithRender,
    GridValueFormatter,
} from '@mui/x-data-grid';
import type { ReactNode } from 'react';

import { formatDateForDisplay } from './DateHelper.tsx';

/**
 * How a feature describes one column to `mapEsriFieldsToDataGridColumns`.
 *
 * Deliberately plain: everything here is understood by every grid that uses it — Bulk
 * Edit, Values and Summaries. A flag only one feature needs belongs with that feature;
 * Summary Reports, for instance, builds its own columns in
 * `components/SummaryReports/utils/summaryReportColumns.ts`.
 */
export interface GridFieldConfiguration {
    field: string;
    editable?: boolean;
    valueFormatter?: GridValueFormatter;
    renderCell?: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => ReactNode;
    alias?: string;
}

export const mapEsriTypeToMuiType = (esriType: string): GridColType => {
    switch (esriType) {
        case 'small-integer':
        case 'integer':
        case 'single':
        case 'double':
        case 'long':
        case 'oid':
        case 'esriFieldTypeSmallInteger':
        case 'esriFieldTypeInteger':
        case 'esriFieldTypeSingle':
        case 'esriFieldTypeDouble':
        case 'esriFieldTypeOID':
            return 'number';
        case 'string':
        case 'guid':
        case 'global-id':
        case 'xml':
        case 'esriFieldTypeString':
        case 'esriFieldTypeGUID':
        case 'esriFieldTypeGlobalID':
        case 'esriFieldTypeXML':
            return 'string';
        case 'date':
        case 'date-only':
        case 'time-only':
        case 'esriFieldTypeDate':
            return 'dateTime';
        case 'geometry':
        case 'blob':
        case 'raster':
        case 'esriFieldTypeGeometry':
        case 'esriFieldTypeBlob':
        case 'esriFieldTypeRaster':
        default:
            return 'string';
    }
};

export const mapEsriFieldsToDataGridColumns = (
    fields: __esri.Field[],
    gridFields: GridFieldConfiguration[] = []
): GridColDef[] => {
    return gridFields.map((field) => {
        const esriField = fields.find((f) => f.name === field.field);

        const muiType = mapEsriTypeToMuiType(esriField?.type || '');

        const colDef: GridColDef = {
            field: field.field,
            headerName: field.alias ? field.alias : esriField?.alias ? esriField.alias : field.field,
            editable: field.editable || false,
            width: 150,
            type: muiType || 'string'
        };

        // format all date fields with the same formatter, can be overridden by field configuration
        if (muiType === 'dateTime') {
            colDef.valueFormatter  = (value => {
                if (!value) return '';
                    return formatDateForDisplay(value)
            })
        }

        if (field.valueFormatter) {
            colDef.valueFormatter = field.valueFormatter
        }

        if(field.renderCell){
            colDef.renderCell = field.renderCell
        }

        return colDef
    });
};
